import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getUserRole } from "../../../utils/getUserRole";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { AdminAccountListItemActions } from "../../Admin/AdminAccountsList/AdminAccountsListItem/AdminAccountsListItem";
import { ListLoading } from "../../SharedComponents/ListLoading/ListLoading";
import { ListStatusDisplay } from "../../SharedComponents/ListStatusDisplay/ListStatusDisplay";
import { OnSSOUserInvitedToAccount } from "../../Subscriptions/onSSOUserInvitedToAccount/onSSOUserInvitedToAccount";
import "./UserAccountUsers.css";

export const UserAccountUsers = ({ accountId }) => {
  const { t } = useTranslation();
  const { data, loading, error } = useQuery(getUsersOnAccountQuery, {
    variables: { accountIds: [accountId] },
    fetchPolicy: "network-only",
  });
  const { data: currentUserData } = useQuery(getUserInformation);
  const location = useLocation();

  useEffect(() => {
    if (error) {
      handleGraphQLErrors(error);
    }
  }, [error]);

  return (
    <div className="UserAccountUsers">
      <OnSSOUserInvitedToAccount />
      <div className="UserAccountUsers__headers">
        <div className="UserAccountUsers__headers--item UserAccountUsers__headers--name">
          {t("generic.name")}
        </div>
        <div className="UserAccountUsers__headers--item UserAccountUsers__headers--status">
          {t("generic.status")}
        </div>
        <div className="UserAccountUsers__headers--item UserAccountUsers__headers--role">
          {t("generic.role")}
        </div>
      </div>
      <div className="UserAccountUsers__users">
        {loading && <ListLoading />}
        {data?.users?.map((user) => {
          return (
            <UserAccountUsersItem
              key={user.id}
              user={user}
              location={location}
              accountId={accountId}
              loggedInUser={currentUserData?.user}
            />
          );
        })}
      </div>
    </div>
  );
};

const getUsersOnAccountQuery = gql`
  query users($accountIds: [String]) {
    users(accountIds: $accountIds) {
      id
      email
      status
      name
      roles
    }
  }
`;

const getUserInformation = gql`
  query user {
    user {
      id
      roles
      name
    }
  }
`;

const listActions = [
  {
    action: "edit-user",
    label: "settings.editUser",
    link: "manage",
  },
];

const UserAccountUsersItem = ({ user, location, accountId, loggedInUser }) => {
  const { t } = useTranslation();
  const [hasMouseOver, setHasMouseOver] = useState(false);

  const loggedInUserRole = loggedInUser?.roles?.sso?.find(
    (x) => x.accountId === accountId
  );

  const userRole = user?.roles?.sso?.find((x) => x.accountId === accountId);

  const formattedUserRole = getUserRole(userRole?.role);
  const formattedStatus = user.status === "ACTIVE" ? "Active" : "Pending";

  return (
    <div
      className="UserAccountUsers__user"
      key={user.id}
      onMouseEnter={() => {
        if (
          loggedInUserRole?.role !== "EMPLOYEE" &&
          loggedInUserRole?.role !== "CUSTOMER"
        ) {
          if (loggedInUser.id !== user.id) setHasMouseOver(true);
        }
      }}
      onMouseLeave={() => {
        if (
          loggedInUserRole?.role !== "EMPLOYEE" &&
          loggedInUserRole?.role !== "CUSTOMER"
        ) {
          if (loggedInUser.id !== user.id) setHasMouseOver(false);
        }
      }}
    >
      <div className="UserAccountUsers__headers--name UserAccountUsers__user--name">
        <div className="UserAccountUsers__user--name-text">
          {user?.name || ""}
        </div>
        <div className="UserAccountUsers__user--email-text">
          {`(${user?.email})`}
        </div>
      </div>
      <div className="UserAccountUsers__headers--status UserAccountUsers__user--status">
        <ListStatusDisplay status={t(formattedStatus)} />
      </div>
      <div className="UserAccountUsers__headers--role UserAccountUsers__user--role">
        {t(formattedUserRole)}
      </div>
      <div className="UserAccountUsers__user--actions">
        <AdminAccountListItemActions
          location={location}
          id={user.id}
          mouseOver={hasMouseOver}
          listActions={listActions}
        />
      </div>
    </div>
  );
};
